<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import FieldConfigCard from './FieldConfigCard.vue';
import CreateCustomSlotDialog from './CreateCustomSlotDialog.vue';
import {
  ALL_SLOT_OPTIONS,
  isCustomSlotOption,
  normalizeCustomSlots,
  normalizeSlotOptions,
} from 'shared/helpers/bookingSlotOptions';

const props = defineProps({
  page: { type: Object, default: null },
  pages: { type: Array, default: () => [] },
  widgetName: { type: String, default: '' },
  spaces: { type: Array, default: () => [] },
  experiences: { type: Array, default: () => [] },
  pricingItems: { type: Array, default: () => [] },
  textOnly: { type: Boolean, default: false },
});

const emit = defineEmits(['update-page', 'save']);
const { t } = useI18n();

const config = computed(() => props.page?.config || {});

const patchPage = patch => {
  emit('update-page', { ...props.page, ...patch });
};

const patchConfig = patch => {
  emit('update-page', {
    ...props.page,
    config: { ...config.value, ...patch },
  });
};

const patchField = (key, field) => {
  patchConfig({
    fields: {
      ...(config.value.fields || {}),
      [key]: field,
    },
  });
};

const spaceOptions = computed(() =>
  (props.spaces || []).map(space => ({
    value: String(space.id),
    label: space.name,
  }))
);

const experienceOptions = computed(() =>
  (props.experiences || []).map(experience => ({
    value: String(experience.id),
    label: experience.name,
  }))
);

const selectedSpaceIds = computed({
  get: () => (config.value.spaceIds || []).map(String),
  set: value => {
    if (props.textOnly) return;
    patchConfig({ spaceIds: (value || []).map(String) });
  },
});

const selectedExperienceIds = computed({
  get: () => (config.value.experienceIds || []).map(String),
  set: value => {
    if (props.textOnly) return;
    patchConfig({ experienceIds: (value || []).map(String) });
  },
});

const showSpaces = computed({
  get: () => config.value.showSpaces !== false,
  set: value => {
    if (props.textOnly) return;
    patchConfig({ showSpaces: !!value });
  },
});

const showExperiences = computed({
  get: () => !!config.value.showExperiences,
  set: value => {
    if (props.textOnly) return;
    patchConfig({ showExperiences: !!value });
  },
});

const showNonMatchingSpaces = computed({
  get: () => config.value.showNonMatchingSpaces !== false,
  set: value => {
    if (props.textOnly) return;
    patchConfig({ showNonMatchingSpaces: !!value });
  },
});

const showNonMatchingExperiences = computed({
  get: () => config.value.showNonMatchingExperiences !== false,
  set: value => {
    if (props.textOnly) return;
    patchConfig({ showNonMatchingExperiences: !!value });
  },
});

const customSlots = computed(() =>
  normalizeCustomSlots(config.value.customSlots)
);

const selectedSlotOptions = computed(() =>
  normalizeSlotOptions(
    config.value.slotOptions || config.value.slot_options,
    customSlots.value
  )
);

const createCustomSlotDialogRef = ref(null);

const customSlotDisplayLabel = def => {
  if (!def) return '';
  let base = def.label;
  if (!base) {
    const minutes = def.durationMinutes || Math.round((def.hours || 0) * 60);
    base = `${minutes}m`;
  }
  if (def.startTime && def.endTime) {
    return `${base} (${def.startTime}–${def.endTime})`;
  }
  return base;
};

const slotOptionLabel = id => {
  if (isCustomSlotOption(id)) {
    return customSlotDisplayLabel(
      customSlots.value.find(slot => slot.id === id)
    );
  }
  return t(`BOOKING_WIDGETS.SLOT_OPTIONS.${String(id).toUpperCase()}`);
};

const isSlotOptionChecked = id => selectedSlotOptions.value.includes(id);

const toggleSlotOption = id => {
  if (props.textOnly) return;
  const current = selectedSlotOptions.value;
  const next = current.includes(id)
    ? current.filter(x => x !== id)
    : [...current, id];
  if (!next.length) return;
  patchConfig({
    slotOptions: normalizeSlotOptions(next, customSlots.value),
    customSlots: customSlots.value,
  });
};

const openCreateCustomSlot = () => {
  if (props.textOnly) return;
  createCustomSlotDialogRef.value?.resetForm?.();
  createCustomSlotDialogRef.value?.dialogRef?.open?.();
};

const onCreateCustomSlot = def => {
  if (props.textOnly || !def) return;
  const nextCustom = normalizeCustomSlots([...customSlots.value, def]);
  const nextOptions = normalizeSlotOptions(
    [...selectedSlotOptions.value, def.id],
    nextCustom
  );
  patchConfig({ slotOptions: nextOptions, customSlots: nextCustom });
  createCustomSlotDialogRef.value?.dialogRef?.close?.();
};

const removeCustomSlot = id => {
  if (props.textOnly) return;
  const nextCustom = customSlots.value.filter(slot => slot.id !== id);
  const nextOptions = normalizeSlotOptions(
    selectedSlotOptions.value.filter(opt => opt !== id),
    nextCustom
  );
  patchConfig({ slotOptions: nextOptions, customSlots: nextCustom });
};

const pageTitle = computed(() => {
  if (!props.page) return '';
  return (
    props.page.label ||
    t(`BOOKING_WIDGETS.SIDEBAR.${props.page.type}`) ||
    props.page.type
  );
});

const showDateSlotOptions = computed(
  () =>
    props.page?.type === 'event_information' &&
    config.value.fields?.date?.showAvailability !== false
);
</script>

<template>
  <section
    class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-n-solid-1"
  >
    <div
      class="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-n-weak px-6"
    >
      <div class="min-w-0 truncate text-lg text-n-slate-11">
        <span class="font-medium text-n-slate-12">{{ widgetName }}</span>
        <span v-if="page"> / {{ pageTitle }}</span>
      </div>
      <Button
        md
        :label="t('BOOKING_WIDGETS.SAVE')"
        @click="emit('save')"
        class="shrink-0"
      />
    </div>

    <div
      v-if="page"
      class="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-n-solid-1 p-6"
    >
      <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
        <h3
          class="mb-4 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
        >
          <Icon
            icon="i-lucide-settings-2"
            class="size-4 shrink-0 text-n-slate-11"
          />
          {{ t('BOOKING_WIDGETS.GENERAL') }}
        </h3>
        <div class="space-y-3">
          <Input
            :model-value="page.title || ''"
            :label="t('BOOKING_WIDGETS.TITLE')"
            class="!text-base font-normal"
            @update:model-value="patchPage({ title: $event })"
          />
          <label class="!mb-2 block text-sm font-medium text-n-slate-12">
            {{ t('BOOKING_WIDGETS.DESCRIPTION_LABEL') }}
          </label>
          <textarea
            rows="3"
            class="!mt-0 w-full !rounded-sm border border-n-weak bg-n-solid-1 px-3 py-2 !leading-tight text-n-slate-12 outline-none focus:border-n-brand !min-h-[76px]"
            :value="page.description || ''"
            @input="patchPage({ description: $event.target.value })"
          />
        </div>
      </div>

      <template v-if="page.type === 'filter'">
        <FieldConfigCard
          title="Attendees"
          icon="i-lucide-users"
          :text-only="textOnly"
          :field="config.fields?.attendees || { enabled: true }"
          @update="patchField('attendees', $event)"
        />
        <FieldConfigCard
          title="Date"
          icon="i-lucide-calendar-check-2"
          :text-only="textOnly"
          show-availability-toggle
          :field="
            config.fields?.date || { enabled: true, showAvailability: true }
          "
          @update="patchField('date', $event)"
        />
      </template>

      <template v-else-if="page.type === 'offering'">
        <p class="text-xs text-n-slate-11">
          {{
            textOnly
              ? t('BOOKING_WIDGETS.INHERITED_STRUCTURE_HELP')
              : t('BOOKING_WIDGETS.OFFERING_HELP')
          }}
        </p>

        <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
          <div class="mb-4 flex items-center justify-between gap-3">
            <h3
              class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
            >
              <Icon
                icon="i-lucide-orbit"
                class="size-4 shrink-0 text-n-slate-11"
              />
              {{ t('BOOKING_WIDGETS.AVAILABLE_SPACES') }}
            </h3>
            <Switch
              :model-value="showSpaces"
              :disabled="textOnly"
              @update:model-value="showSpaces = $event"
            />
          </div>

          <div v-if="showSpaces" class="space-y-3">
            <p class="text-xs text-n-slate-11">
              {{ t('BOOKING_WIDGETS.AVAILABLE_SPACES_HELP') }}
            </p>
            <TagMultiSelectComboBox
              v-model="selectedSpaceIds"
              :options="spaceOptions"
              :disabled="textOnly"
              :placeholder="t('BOOKING_WIDGETS.ADD_SPACE')"
              :search-placeholder="t('BOOKING_WIDGETS.SEARCH_SPACES')"
              :empty-state="t('BOOKING_WIDGETS.NO_SPACES')"
            />
            <div :class="{ 'opacity-60': textOnly }">
              <label class="flex items-center gap-2 text-sm text-n-slate-12">
                <input
                  v-model="showNonMatchingSpaces"
                  type="checkbox"
                  class="rounded border-n-weak"
                  :disabled="textOnly"
                />
                {{ t('BOOKING_WIDGETS.SHOW_NON_MATCHING_SPACES') }}
              </label>
              <p class="mt-0.5 pl-6 text-xs text-n-slate-11">
                {{ t('BOOKING_WIDGETS.SHOW_NON_MATCHING_SPACES_HELP') }}
              </p>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
          <div class="mb-4 flex items-center justify-between gap-3">
            <h3
              class="flex items-center gap-2 text-sm font-semibold text-n-slate-12"
            >
              <Icon
                icon="i-lucide-sparkles"
                class="size-4 shrink-0 text-n-slate-11"
              />
              {{ t('BOOKING_WIDGETS.AVAILABLE_EXPERIENCES') }}
            </h3>
            <Switch
              :model-value="showExperiences"
              :disabled="textOnly"
              @update:model-value="showExperiences = $event"
            />
          </div>

          <div v-if="showExperiences" class="space-y-3">
            <p class="text-xs text-n-slate-11">
              {{ t('BOOKING_WIDGETS.AVAILABLE_EXPERIENCES_HELP') }}
            </p>
            <TagMultiSelectComboBox
              v-model="selectedExperienceIds"
              :options="experienceOptions"
              :disabled="textOnly"
              :placeholder="t('BOOKING_WIDGETS.ADD_EXPERIENCE')"
              :search-placeholder="t('BOOKING_WIDGETS.SEARCH_EXPERIENCES')"
              :empty-state="t('BOOKING_WIDGETS.NO_EXPERIENCES')"
            />
            <div :class="{ 'opacity-60': textOnly }">
              <label class="flex items-center gap-2 text-sm text-n-slate-12">
                <input
                  v-model="showNonMatchingExperiences"
                  type="checkbox"
                  class="rounded border-n-weak"
                  :disabled="textOnly"
                />
                {{ t('BOOKING_WIDGETS.SHOW_NON_MATCHING_EXPERIENCES') }}
              </label>
              <p class="mt-0.5 pl-6 text-xs text-n-slate-11">
                {{ t('BOOKING_WIDGETS.SHOW_NON_MATCHING_EXPERIENCES_HELP') }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="page.type === 'event_information'">
        <p class="text-xs text-n-slate-11">
          {{
            textOnly
              ? t('BOOKING_WIDGETS.INHERITED_STRUCTURE_HELP')
              : t('BOOKING_WIDGETS.EVENT_INFORMATION_HELP')
          }}
        </p>
        <FieldConfigCard
          title="Event Name"
          icon="i-lucide-text-cursor-input"
          :text-only="textOnly"
          :field="config.fields?.eventName || { enabled: true }"
          @update="patchField('eventName', $event)"
        />
        <FieldConfigCard
          title="Attendees"
          icon="i-lucide-users"
          :text-only="textOnly"
          :field="config.fields?.attendees || { enabled: true }"
          @update="patchField('attendees', $event)"
        />
        <FieldConfigCard
          title="Date"
          icon="i-lucide-calendar-check-2"
          :text-only="textOnly"
          show-availability-toggle
          :field="
            config.fields?.date || { enabled: true, showAvailability: true }
          "
          @update="patchField('date', $event)"
        >
          <div v-if="showDateSlotOptions">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('BOOKING_WIDGETS.SLOT_OPTIONS.LABEL') }}
            </span>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('BOOKING_WIDGETS.SLOT_OPTIONS.HELP') }}
            </p>
            <div class="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
              <label
                v-for="optId in ALL_SLOT_OPTIONS"
                :key="optId"
                class="flex cursor-pointer select-none items-center gap-1.5 text-xs text-n-slate-12"
                :class="{ 'opacity-60': textOnly }"
              >
                <input
                  type="checkbox"
                  class="rounded border-n-weak"
                  :checked="isSlotOptionChecked(optId)"
                  :disabled="textOnly"
                  @change="toggleSlotOption(optId)"
                />
                {{ slotOptionLabel(optId) }}
              </label>
            </div>

            <div
              v-if="customSlots.length"
              class="mt-3 space-y-1.5 border-t border-n-weak pt-3"
            >
              <div
                v-for="slot in customSlots"
                :key="slot.id"
                class="flex items-center justify-between gap-2"
              >
                <label
                  class="flex min-w-0 cursor-pointer select-none items-center gap-1.5 text-xs text-n-slate-12"
                  :class="{ 'opacity-60': textOnly }"
                >
                  <input
                    type="checkbox"
                    class="rounded border-n-weak"
                    :checked="isSlotOptionChecked(slot.id)"
                    :disabled="textOnly"
                    @change="toggleSlotOption(slot.id)"
                  />
                  <span class="truncate">{{ customSlotDisplayLabel(slot) }}</span>
                </label>
                <button
                  type="button"
                  class="shrink-0 text-xs text-n-slate-11 hover:text-n-ruby-11"
                  :disabled="textOnly"
                  @click="removeCustomSlot(slot.id)"
                >
                  {{ t('BOOKING_WIDGETS.SLOT_OPTIONS.REMOVE_CUSTOM') }}
                </button>
              </div>
            </div>

            <div class="mt-3">
              <Button
                sm
                slate
                faded
                :label="t('BOOKING_WIDGETS.SLOT_OPTIONS.CREATE_CUSTOM')"
                :disabled="textOnly"
                @click="openCreateCustomSlot"
              />
            </div>
            <CreateCustomSlotDialog
              ref="createCustomSlotDialogRef"
              @create="onCreateCustomSlot"
            />
          </div>
        </FieldConfigCard>
      </template>

      <template v-else-if="page.type === 'contact_information'">
        <FieldConfigCard
          title="Name"
          icon="i-lucide-id-card"
          :text-only="textOnly"
          :field="config.fields?.name || { enabled: true }"
          @update="patchField('name', $event)"
        />
        <FieldConfigCard
          title="Email"
          icon="i-lucide-mail"
          :text-only="textOnly"
          :field="config.fields?.email || { enabled: true }"
          @update="patchField('email', $event)"
        />
        <FieldConfigCard
          title="Phone"
          icon="i-lucide-phone"
          :text-only="textOnly"
          :field="config.fields?.phone || { enabled: true }"
          @update="patchField('phone', $event)"
        />
      </template>
    </div>
  </section>
</template>
